import { createBrowserRouter } from 'react-router-dom'

import { ConsoleShell } from './console/ConsoleShell.tsx'
import { KitchenDayDocView } from './console/print/KitchenDayDoc.tsx'
import { KitchenDocView } from './console/print/KitchenDoc.tsx'
import { Alerts } from './console/views/Alerts.tsx'
import { Calibration } from './console/views/Calibration.tsx'
/* A6 of the catalogue brief of 19 October 2026: the catalogue page, with the
   publish button and the last twenty sync runs. */
import { Catalogue } from './console/views/Catalogue.tsx'
import { Callbacks } from './console/views/Callbacks.tsx'
/* `build/FIXES.md` item 79 (F8): the confirm-first queue, beside the review
   queue rather than instead of it. */
import { Confirmations } from './console/views/Confirmations.tsx'
import { Day } from './console/views/Day.tsx'
import { Dietary } from './console/views/Dietary.tsx'
import { Editor } from './console/views/Editor.tsx'
import { Handover } from './console/views/Handover.tsx'
import { OrderDetail } from './console/views/OrderDetail.tsx'
import { Orders } from './console/views/Orders.tsx'
import { Overrides } from './console/views/Overrides.tsx'
import { Home } from './console/views/Placeholder.tsx'
import { Refunds } from './console/views/Refunds.tsx'
import { Review } from './console/views/Review.tsx'
import { Settings } from './console/views/Settings.tsx'
import { CustomerPage } from './page/CustomerPage.tsx'
import { Layout } from './routes/Layout.tsx'
import { MockPaymentPage } from './routes/MockPaymentPage.tsx'
import { NotFound } from './routes/NotFound.tsx'
import { ReturnPage } from './routes/ReturnPage.tsx'
import { SurveyPage } from './routes/SurveyPage.tsx'
import { StaffEmails } from './routes/StaffEmails.tsx'

/**
 * Release one's routes.
 *
 * `/` is the ordering page of §5. `/fizetes/{token}` is the back URL of §9.4,
 * which is display only and never changes an order's status.
 * `/fizetes-mock/{token}` is the mock payment driver that stands in for the
 * SimplePay page until `045_live_drivers` brings the real one; it says on
 * every screen that it is not a payment page.
 *
 * Everything under `/staff` is the console of §11.7, and it is deliberately
 * not under the customer page's `Layout`: `docs/overnight_constraints.md` §1
 * is that there is no internal or admin view in the customer page, and the
 * cleanest way to keep that true is for the two to share no shell, no
 * stylesheet and no state. `/staff/emails` is 028's template preview, which
 * reads nothing and needs no session; the rest is behind `ConsoleShell`, which
 * signs the person in and draws the menu `public.console_menu` gives them.
 *
 * `/staff/orders/:orderId/nyomtatas/:kind` is the A4 kitchen print view of
 * §11.6. It hangs off the console but outside its chrome, because the thing
 * that comes out of the printer must be the document and not a screenshot of
 * an application. `/staff/nyomtatas/:kind` is the same for K5 and K7, which
 * are about a day rather than an order and take it on `?nap=YYYY-MM-DD`.
 *
 * `/staff/editor` is the staff editor of §11.11. It takes the order on
 * `?order=<id>` rather than in the path, because §11.11.2 also opens it with
 * no order at all — a new order taken by phone — and a route that required
 * one would have to be two routes.
 *
 * `/kerdoiv/{token}` is §11.10's post-event survey, reached from U13 by its own
 * token. It sits under the customer `Layout` and not under `/staff`, because
 * it is a customer page: it carries the order's reference and date and nothing
 * else off the order.
 *
 * Every view `console_menu` returns now has a route. `NotBuilt` is still
 * exported for whatever a later stage leaves unfinished; nothing uses it here.
 */
export const routes = [
  {
    path: '/',
    element: <Layout />,
    children: [
      { index: true, element: <CustomerPage /> },
      { path: 'fizetes/:token', element: <ReturnPage /> },
      { path: 'fizetes-mock/:token', element: <MockPaymentPage /> },
      { path: 'kerdoiv/:token', element: <SurveyPage /> },
      { path: 'staff/emails', element: <StaffEmails /> },
    ],
  },
  { path: '/staff/orders/:orderId/nyomtatas/:kind', element: <KitchenDocView /> },
  { path: '/staff/nyomtatas/:kind', element: <KitchenDayDocView /> },
  {
    path: '/staff',
    element: <ConsoleShell />,
    children: [
      { index: true, element: <Home /> },
      { path: 'confirmations', element: <Confirmations /> },
      { path: 'review', element: <Review /> },
      { path: 'dietary', element: <Dietary /> },
      { path: 'orders', element: <Orders /> },
      { path: 'orders/:orderId', element: <OrderDetail /> },
      { path: 'callbacks', element: <Callbacks /> },
      { path: 'refunds', element: <Refunds /> },
      { path: 'day', element: <Day /> },
      { path: 'settings', element: <Settings /> },
      { path: 'editor', element: <Editor /> },
      { path: 'overrides', element: <Overrides /> },
      { path: 'handover', element: <Handover /> },
      { path: 'alerts', element: <Alerts /> },
      { path: 'calibration', element: <Calibration /> },
      { path: 'catalogue', element: <Catalogue /> },
    ],
  },
  { path: '*', element: <NotFound /> },
]

export const router = createBrowserRouter(routes)
